import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Ship, ClipboardList, User, LogOut, CreditCard, Settings } from 'lucide-angular';
import { AuthService } from '@marina/auth';
import { UserDto } from '@marina/models';
import { Observable, Subject } from 'rxjs';
import { takeUntil, map, filter } from 'rxjs/operators';
import { APP_VERSION } from '../../../version';
import { CustomerService } from '@marina/api-client';
import { PortalCustomerContextService } from '../../../services/portal-customer-context.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  currentUser$: Observable<UserDto | null>;
  currentRoute = '';

  // Lucide icon components
  Ship = Ship;
  ClipboardList = ClipboardList;
  User = User;
  LogOut = LogOut;
  CreditCard = CreditCard;
  Settings = Settings;

  // Version from version.ts
  version = `v${APP_VERSION}`;

  private destroy$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    private router: Router,
    private customerService: CustomerService,
    readonly portalContext: PortalCustomerContextService
  ) {
    this.currentUser$ = this.authService.currentUser$.pipe(takeUntil(this.destroy$));

    // Track current route for active state
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
      });
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.portalContext.ensureLoaded(this.customerService).pipe(takeUntil(this.destroy$)).subscribe({
      error: (e: unknown) => console.warn('Portal role prefetch failed', e),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUserInitials(): string {
    const user = this.authService.currentUserValue;
    if (!user || !user.name) {
      return 'U';
    }
    const names = user.name.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  }

  isRouteActive(route: string): boolean {
    return this.currentRoute === route || this.currentRoute.startsWith(route + '/');
  }

  logout(): void {
    this.portalContext.clear();
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.router.navigate(['/auth/login']);
      },
    });
  }
}

